import { PLACEHOLDER_IMAGES } from '../../utils/placeholderImages';
import type {
  InviteContent,
  InviteTheme,
  InviteSectionsEnabled,
  InviteImage,
  RsvpSummary,
  GuestWithInvite,
} from '../../types';

// ---------------------------------------------------------------------------
// Sample invite data for live template preview
// ---------------------------------------------------------------------------
export const sampleTheme: InviteTheme = {
  preset: 'sage',
  colors: {
    background: '#f8faf6',
    accent: '#8fa382',
    headingText: '#3d4a3d',
    bodyText: '#5d6b58',
    nav: '#5d6b58',
    button: '#8fa382',
  },
  fonts: {
    heading: 'Cormorant Garamond',
    body: 'Quicksand',
  },
};

export const embossedSampleTheme: InviteTheme = {
  preset: 'embossed',
  colors: {
    background: '#c4a5a5',
    accent: '#a58585',
    headingText: '#ffffff',
    bodyText: '#ffffff',
    nav: '#ffffff',
    button: '#8a6a6a',
  },
  fonts: {
    heading: 'Sacramento',
    body: 'Josefin Sans',
  },
};

export const floralSampleTheme: InviteTheme = {
  preset: 'floral',
  colors: {
    background: '#fdf9f3',
    accent: '#c4826a',
    headingText: '#5a4038',
    bodyText: '#7a5a4a',
    nav: '#7a5a4a',
    button: '#c4826a',
  },
  fonts: {
    heading: 'Playfair Display',
    body: 'Quicksand',
  },
};

export const geometricSampleTheme: InviteTheme = {
  preset: 'geometric',
  colors: {
    background: '#f5f3ef',
    accent: '#16565f',
    headingText: '#2a3a3f',
    bodyText: '#4a5a5f',
    nav: '#16565f',
    button: '#16565f',
  },
  fonts: {
    heading: 'Playfair Display',
    body: 'Lato',
  },
  pattern: 'diagonal-stripes',
};

export const polaroidSampleTheme: InviteTheme = {
  preset: 'polaroid',
  colors: {
    background: '#f9f4ef',
    accent: '#c4826a',
    headingText: '#3a2e28',
    bodyText: '#6a5a50',
    nav: '#6a5a50',
    button: '#c4826a',
  },
  fonts: {
    heading: 'Playfair Display',
    body: 'Quicksand',
  },
};

export const sketchSampleTheme: InviteTheme = {
  preset: 'sketch',
  colors: {
    background: '#fdf9f4',
    accent: '#8c6d4f',
    headingText: '#3d2e20',
    bodyText: '#6b5840',
    nav: '#6b5840',
    button: '#8c6d4f',
  },
  fonts: {
    heading: 'Cormorant Garamond',
    body: 'Quicksand',
  },
  paletteId: 'sketch-parchment',
};

export const luxurySampleTheme: InviteTheme = {
  preset: 'luxury',
  colors: {
    background: '#FAF7F2',
    accent: '#B8975A',
    headingText: '#2C2520',
    bodyText: '#6B5744',
    nav: '#2C2520',
    button: '#B8975A',
    darkBackground: '#2C2520',
  },
  fonts: {
    heading: 'Cormorant Garamond',
    body: 'Jost',
  },
  luxuryIllustration: 'venue',
};

export const sampleContent: InviteContent = {
  hero: {
    title: 'Emma & James',
    subtitle: 'Together with their families',
    dateDisplay: 'Saturday, 14th June 2025',
    backgroundImageId: null,
    showCountdown: true,
  },
  details: {
    locations: [
      {
        id: '1',
        label: 'Ceremony',
        name: 'St Mary\'s Church',
        address: 'Church Lane, Cotswolds, GL54 1AA',
        showMap: false,
        coordinates: null,
      },
      {
        id: '2',
        label: 'Reception',
        name: 'The Barn at Upcote',
        address: 'Withington, Cheltenham, GL54 4BN',
        showMap: false,
        coordinates: null,
      },
    ],
    dressCode: 'Smart Casual',
    additionalInfo: 'We can\'t wait to celebrate with you!',
  },
  schedule: {
    items: [
      { id: '1', time: '13:00', description: 'Ceremony begins' },
      { id: '2', time: '14:30', description: 'Drinks reception' },
      { id: '3', time: '16:00', description: 'Wedding breakfast' },
      { id: '4', time: '19:00', description: 'Evening celebration' },
    ],
  },
  gallery: { imageIds: [-1, -2, -3] },
  rsvp: {
    collectEmail: true,
    collectDietary: true,
    allowPlusOnes: true,
    maxPlusOnes: 1,
    customMessage: 'Please let us know if you can make it!',
  },
  faq: { items: [] },
  gifts: { title: '', description: '', registryUrl: '', registryLabel: '' },
};

export const sampleSectionsEnabled: InviteSectionsEnabled = {
  hero: true,
  details: true,
  schedule: true,
  gallery: true,
  rsvp: true,
  faq: false,
  gifts: false,
  nearby: false,
};

export const sampleImages: InviteImage[] = PLACEHOLDER_IMAGES.slice(0, 3);

// ---------------------------------------------------------------------------
// Anniversary demo data
// ---------------------------------------------------------------------------

export const anniversaryTypographicTheme: InviteTheme = {
  preset: 'typographic',
  colors: {
    background: '#FAF8F4',
    accent: '#b8a88c',
    headingText: '#1e1c18',
    bodyText: '#6a6050',
    nav: '#1e1c18',
    button: '#b8a88c',
  },
  fonts: { heading: 'Cormorant Garamond', body: 'Jost' },
  paletteId: 'typographic-parchment',
  typographicHeroLayout: 'l2-stacked',
  typographicMonogramStyle: 'side-by-side',
  typographicSectionInvert: [],
};

export const anniversarySageTheme: InviteTheme = {
  preset: 'sage',
  colors: {
    background: '#f8faf6',
    accent: '#8fa382',
    headingText: '#3d4a3d',
    bodyText: '#5d6b58',
    nav: '#5d6b58',
    button: '#8fa382',
  },
  fonts: {
    heading: 'Cormorant Garamond',
    body: 'Quicksand',
  },
};

export const anniversary25thContent: InviteContent = {
  hero: {
    title: 'Sarah & Thomas',
    subtitle: 'Twenty-five wonderful years',
    dateDisplay: 'Saturday, 15th August 2026',
    backgroundImageId: null,
    showCountdown: false,
  },
  details: {
    locations: [
      {
        id: '1',
        label: 'Venue',
        name: 'The Old Mill',
        address: 'Mill Lane, Cirencester, GL7 1JH',
        showMap: false,
        coordinates: null,
      },
    ],
    dressCode: 'Smart Casual',
    additionalInfo: 'We look forward to celebrating with you.',
  },
  schedule: {
    items: [
      { id: '1', time: '18:00', description: 'Drinks & canapés' },
      { id: '2', time: '19:30', description: 'Dinner' },
      { id: '3', time: '21:00', description: 'Dancing & celebration' },
    ],
  },
  gallery: { imageIds: [-1, -2, -3] },
  rsvp: {
    collectEmail: true,
    collectDietary: false,
    allowPlusOnes: true,
    maxPlusOnes: 1,
    customMessage: 'Please let us know if you can join us.',
  },
  faq: { items: [] },
  gifts: { title: '', description: '', registryUrl: '', registryLabel: '' },
};

export const anniversary10thContent: InviteContent = {
  hero: {
    title: 'Helen & David',
    subtitle: 'A decade together',
    dateDisplay: 'Friday, 12th September 2026',
    backgroundImageId: null,
    showCountdown: false,
  },
  details: {
    locations: [
      {
        id: '1',
        label: 'Venue',
        name: 'Rosewood House',
        address: 'Park Avenue, Bath, BA1 2JD',
        showMap: false,
        coordinates: null,
      },
    ],
    dressCode: 'Cocktail',
    additionalInfo: "Can't wait to celebrate with you all.",
  },
  schedule: {
    items: [
      { id: '1', time: '19:00', description: 'Welcome drinks' },
      { id: '2', time: '20:00', description: 'Dinner' },
      { id: '3', time: '22:00', description: 'Celebrations continue' },
    ],
  },
  gallery: { imageIds: [-1, -2] },
  rsvp: {
    collectEmail: true,
    collectDietary: false,
    allowPlusOnes: false,
    maxPlusOnes: 0,
    customMessage: 'Kindly RSVP by 1st August.',
  },
  faq: { items: [] },
  gifts: { title: '', description: '', registryUrl: '', registryLabel: '' },
};

export const anniversarySectionsEnabled: InviteSectionsEnabled = {
  hero: true,
  details: true,
  schedule: true,
  gallery: false,
  rsvp: true,
  faq: false,
  gifts: false,
  nearby: false,
};

// Color palettes for the customisation showcase
export const colorCycles = [
  { accent: '#8fa382', headingText: '#3d4a3d', bodyText: '#5d6b58', background: '#f8faf6', nav: '#5d6b58', button: '#8fa382' },
  { accent: '#c4a27c', headingText: '#4a3d2d', bodyText: '#6b5d4a', background: '#fdf8f4', nav: '#6b5d4a', button: '#c4a27c' },
  { accent: '#6b7c8d', headingText: '#3d4654', bodyText: '#586b7c', background: '#f4f6fa', nav: '#586b7c', button: '#6b7c8d' },
  { accent: '#b8738a', headingText: '#4a3d42', bodyText: '#6b585e', background: '#faf4f7', nav: '#6b585e', button: '#b8738a' },
];

// Mock RSVP data for the guest management showcase
export const mockRsvpTotals: RsvpSummary['totals'] = {
  total_guests: 107,
  total_invited: 95,
  attending: 72,
  not_attending: 8,
  maybe: 5,
  pending: 10,
  total_plus_ones: 12,
};

export const mockRecentResponses: RsvpSummary['recent_responses'] = [
  { id: 1, name: 'Emily Watson', email: 'emily@example.com', rsvp_status: 'yes', plus_count: 0, dietary_notes: null, rsvp_submitted_at: new Date().toISOString(), invite_id: 1, invite_title: '' },
  { id: 2, name: 'David Chen', email: 'david@example.com', rsvp_status: 'yes', plus_count: 0, dietary_notes: 'Vegetarian', rsvp_submitted_at: new Date(Date.now() - 3600000).toISOString(), invite_id: 1, invite_title: '' },
  { id: 3, name: 'Sarah Miller', email: 'sarah@example.com', rsvp_status: 'maybe', plus_count: 0, dietary_notes: null, rsvp_submitted_at: new Date(Date.now() - 7200000).toISOString(), invite_id: 1, invite_title: '' },
];

const mockGuestBase = {
  invite_id: 1, token: '', dietary_notes: null, dietary_preference: null, allergies: null,
  parent_guest_id: null, parent_guest_name: null, view_count: 2, first_viewed_at: null, last_viewed_at: null,
  created_at: new Date().toISOString(), updated_at: new Date().toISOString(), public_url: '',
  unsubscribed_at: null, tags: undefined,
  invite: { id: 1, title: 'Wedding Invitation', slug: 'wedding', status: 'published' as const, visibility: 'private' as const, is_system: false },
};

export const mockGuests: GuestWithInvite[] = [
  { ...mockGuestBase, id: 1, name: 'Emily Watson', email: 'emily@example.com', rsvp_status: 'yes' as const, plus_count: 1, rsvp_submitted_at: new Date().toISOString(), email_sent_at: new Date(Date.now() - 172800000).toISOString(), token: 'a' } as GuestWithInvite,
  { ...mockGuestBase, id: 2, name: 'David Chen', email: 'david@example.com', rsvp_status: 'yes' as const, plus_count: 0, rsvp_submitted_at: new Date().toISOString(), email_sent_at: new Date(Date.now() - 172800000).toISOString(), token: 'b' } as GuestWithInvite,
  { ...mockGuestBase, id: 3, name: 'Sarah Miller', email: 'sarah@example.com', rsvp_status: 'maybe' as const, plus_count: 0, rsvp_submitted_at: new Date().toISOString(), email_sent_at: new Date(Date.now() - 172800000).toISOString(), token: 'c' } as GuestWithInvite,
  { ...mockGuestBase, id: 4, name: 'Tom Brown', email: 'tom@example.com', rsvp_status: 'pending' as const, plus_count: 0, rsvp_submitted_at: null, email_sent_at: new Date(Date.now() - 172800000).toISOString(), token: 'd' } as GuestWithInvite,
  { ...mockGuestBase, id: 5, name: 'Lisa Park', email: 'lisa@example.com', rsvp_status: 'pending' as const, plus_count: 0, rsvp_submitted_at: null, email_sent_at: null, token: 'e' } as GuestWithInvite,
];
